// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { Cart } from "@insite/client-framework/Services/CartService";
import { GetProductCollectionApiV2Parameter } from "@insite/client-framework/Services/ProductServiceV2";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import {
    canCheckoutWithCart,
    hasOnlyQuoteRequiredProducts,
    isCartCheckoutDisabled,
} from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getCurrentPage, getHeader, getPageStateByType } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { nullPage } from "@insite/client-framework/Store/Data/Pages/PagesState";
import { getProductsDataView } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import { getWidgetsByPageId } from "@insite/client-framework/Store/Data/Widgets/WidgetSelectors";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { xHasRoles } from "@pacific/App/Store/Selectors/SessionSelectors";
import { USER_ROLES } from "@pacific/XDL/constants";

export const getLogoImage = (state: ApplicationState) => {
    const logoImage = getWidgetsByPageId(state, getHeader(state).id).find((widget: WidgetProps) => {
        return widget.type === "Basic/Logo";
    })?.fields.logoImage;
    return logoImage;
};

export const xIsAuthenticated = (state: ApplicationState): boolean => {
    const session = getSession(state);
    return session && session.isAuthenticated && !session.isGuest;
};

export const xisCheckoutDisabled = (cart: Cart | undefined) => {
    return !canCheckoutWithCart(cart) || hasOnlyQuoteRequiredProducts(cart) || isCartCheckoutDisabled(cart);
};

export const getMainNavigationWidget = (state: ApplicationState) => {
    return getWidgetsByPageId(state, getHeader(state).id).find((widget: WidgetProps) => {
        return widget.type === "Header/MainNavigation";
    })?.fields;
};

export const getHeaderSignInWidget = (state: ApplicationState) => {
    return getWidgetsByPageId(state, getHeader(state).id).find((widget: WidgetProps) => {
        return widget.type === "Common/HeaderSignIn";
    })?.fields;
};

export const getCartLinkWidget = (state: ApplicationState) => {
    return getWidgetsByPageId(state, getHeader(state).id).find((widget: WidgetProps) => {
        return widget.type === "Common/CartLink";
    })?.fields;
};

export const getLogoImageWidget = (state: ApplicationState) => {
    return getWidgetsByPageId(state, getHeader(state).id).find((widget: WidgetProps) => {
        return widget.type === "Basic/Logo";
    })?.fields;
};

export const getHeaderActionsWidget = (state: ApplicationState) => {
    return getWidgetsByPageId(state, getHeader(state).id).find((widget: WidgetProps) => {
        return widget.type === "HeaderActions/HeaderActions";
    })?.fields;
};

export const xIsAdminstrator = (state: ApplicationState): boolean => {
    const session = getSession(state);
    return xIsAuthenticated(state) && session.userRoles?.toLowerCase().indexOf("administrator") !== -1;
};

export const getProductList = (state: ApplicationState) => {
    return getPageStateByType(state, "ProductListPage").value || nullPage;
};

export const getProductListViewWidget = (state: ApplicationState) => {
    return getWidgetsByPageId(state, getProductList(state).id).find((widget: WidgetProps) => {
        return widget.type === "ProductList/ProductListViewSelect";
    })?.fields;
};

export const getSecondaryNavigationWidget = (state: ApplicationState) => {
    return getWidgetsByPageId(state, getHeader(state).id).find((widget: WidgetProps) => {
        return widget.type === "Header/SecondaryNavigation";
    })?.fields;
};
export const getHeaderLinkListWidget = (state: ApplicationState) => {
    return getWidgetsByPageId(state, getHeader(state).id).find((widget: WidgetProps) => {
        return widget.type === "Header/HeaderLinkList";
    })?.fields;
};

export const XngGetProductListDataView = (state: ApplicationState, param?: GetProductCollectionApiV2Parameter) => {
    const { parameter, lastParameter } = state.pages.productList;
    let productsDataView = getProductsDataView(state, { ...parameter, ...param });
    if (!productsDataView.value) {
        productsDataView = getProductsDataView(state, { ...lastParameter, ...param });
    }

    return productsDataView;
};

export const getIsRecentlyPurchasedPage = (state: ApplicationState) => {
    const pageType = getCurrentPage(state).type;
    return pageType === "RecentlyPurchasedProductsPage";
};

export const canSeeBillTrustLink = (state: ApplicationState): boolean | undefined => {
    return xHasRoles(state, [USER_ROLES.ADMIN, USER_ROLES.FINANCE], "one-or-more");
};
